import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { getEndOfMonthDate, getStartOfMonthDate } from "../lib/getStartEndDate";
import axios from "../lib/axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import moment from "moment-jalaali";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import DueBills from "../components/dashboard/DueBills";
import DuePurchases from "../components/dashboard/DuePurchases";
import LastExpenses from "../components/dashboard/LastExpenses";
import DonutsChart1 from "../components/dashboard/DonutsChart1";
import DonutsChart2 from "../components/dashboard/DonutsChart2";
import DonutsChart4 from "../components/dashboard/DonutsChart4";
import DonutsChart3 from "../components/dashboard/DonutsChart3";

// Setup moment for Jalali
moment.loadPersian({ dialect: "persian-modern" });
const localizer = momentLocalizer(moment);

const eventStyleGetter = (event, start, end, isSelected) => {
  let backgroundColor = "#f0f0f0";

  // Convert current date to Jalali for comparison
  const now = moment();
  const eventStart = moment(event.start);

  if (eventStart.isBefore(now) && event.balance_amount == 0) {
    backgroundColor = "#3CB371"; // Green - Paid past bills
  } else if (eventStart.isBefore(now)) {
    backgroundColor = "#F08080"; // Red - Unpaid past bills
  } else {
    backgroundColor = "#87CEFA"; // Blue - Future bills
  }

  const style = {
    backgroundColor,
    borderRadius: "4px",
    opacity: 0.8,
    color: "white",
    border: "0px",
    display: "block",
    padding: "2px 5px",
    cursor: "pointer",
  };

  return { style };
};

const customMessages = {
  today: "امروز",
  previous: "قبلی",
  next: "بعدی",
  month: "ماه",
  week: "هفته",
  day: "روز",
};

const views = {
  month: true,
};

function Dashboard() {
  const [bills, setBills] = useState([]);
  const [billsGrowth, setBillsGrowth] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    getBills();
    getBillGrowth();
  }, []);

  const handleNavigate = (newDate, view) => {
    // newDate is a Date object, pass it directly
    getBills(newDate);
  };

  const getBillGrowth = () => {
    axios
      .get(`/api/dashboard/bill-growth`)
      .then((data) => {
        const d = data.map((row) => ({ ...row, value: parseFloat(row.value) }));
        setBillsGrowth(d);

        console.log(data);
      })
      .catch((error) => {
        toast.error(error.message);
        console.log(error);
      });
  };

  const getBills = (date = new Date()) => {
    // Convert to Jalali format for API request
    const startJalali = moment(date).startOf("jMonth").format("jYYYY-jMM-jDD");
    const endJalali = moment(date).endOf("jMonth").format("jYYYY-jMM-jDD");

    console.log("Jalali date range:", startJalali, "to", endJalali);

    axios
      .post(`/api/dashboard/bills`, {
        start: startJalali, // Send Jalali dates to backend
        end: endJalali,
      })
      .then((data) => {
        const d = data.map((row) => ({
          ...row,
          // Convert Jalali date string from backend to Date object
          start: moment(row.start, "jYYYY-jMM-jDD").toDate(),
          end: moment(row.end, "jYYYY-jMM-jDD").toDate(),
        }));
        setBills(d);
        console.log(data);
      })
      .catch((error) => {
        toast.error(error.message);
        console.log(error);
      });
  };

  const CustomEvent = ({ event }) => {
    return (
      <div style={{ whiteSpace: "normal", direction: "rtl", fontSize: "12px" }}>
        <strong>{event.title}</strong>
      </div>
    );
  };

  const [events, setEvents] = useState([]);

  const fetchEvents = async (start, end) => {
    try {
      // Convert moment dates to Jalali string
      const startJalali = moment(start).format("jYYYY-jMM-jDD");
      const endJalali = moment(end).format("jYYYY-jMM-jDD");

      const response = await fetch(
        `/api/bills?start=${startJalali}&end=${endJalali}`,
      );
      const data = await response.json();

      // Convert Jalali dates to moment objects
      const formattedEvents = data.map((event) => ({
        ...event,
        start: moment(`${event.start}`, "jYYYY-jMM-jDD").toDate(),
        end: moment(`${event.end}`, "jYYYY-jMM-jDD").toDate(),
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  useEffect(() => {
    const now = moment();
    const start = now.startOf("jMonth").toDate();
    const end = now.endOf("jMonth").toDate();
    fetchEvents(start, end);
  }, []);

  const messages = {
    today: "امروز",
    previous: "قبلی",
    next: "بعدی",
    month: "ماه",
    week: "هفته",
    day: "روز",
    agenda: "برنامه",
    date: "تاریخ",
    time: "زمان",
    event: "رویداد",
    noEventsInRange: "هیچ رویدادی در این بازه وجود ندارد",
  };

  return (
    <div>
      <div className="flex bg-slate-50 border justify-between py-2 px-4">
        <h3>داشبورد</h3>
      </div>

      <div className="flex flex-wrap my-6">
        <div className="lg:w-1/4 md:w-1/2 sm:w-1/2 p-4">
          <DonutsChart1 />
        </div>
        <div className="lg:w-1/4 md:w-1/2 sm:w-1/2 p-4">
          <DonutsChart2 />
        </div>
        <div className="lg:w-1/4 md:w-1/2 sm:w-1/2 p-4">
          <DonutsChart3 />
        </div>
        <div className="lg:w-1/4 md:w-1/2 sm:w-1/2 p-4">
          <DonutsChart4 />
        </div>
      </div>
      <div className=" flex flex-col lg:flex-row gap-4 p-4">
        <div className="lg:w-1/2">
          <div className=" border rounded-t-sm" dir="ltr">
            <p
              className="bg-gray-50 border-b rounded-t-sm px-2 py-1 mb-4"
              dir="rtl"
            >
              مشاهده روی جنتری
            </p>
            <div className="p-2" style={{ direction: "rtl" }}>
              <Calendar
                localizer={localizer}
                events={bills}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={(e) => navigate(`/sales/${e.id}/edit`)}
                eventPropGetter={eventStyleGetter}
                onNavigate={handleNavigate}
                components={{
                  event: CustomEvent,
                }}
                messages={messages}
                views={["month", "week", "day"]}
                defaultView="month"
                culture="fa"
                formats={{
                  // Month header format (Month Year)
                  monthHeaderFormat: (date) => {
                    const dariMonths = [
                      "حمل",
                      "ثور",
                      "جوزا",
                      "سرطان",
                      "اسد",
                      "سنبله",
                      "میزان",
                      "عقرب",
                      "قوس",
                      "جدی",
                      "دلو",
                      "حوت",
                    ];
                    const jMonth = moment(date).jMonth();
                    const jYear = moment(date).jYear();
                    return `${dariMonths[jMonth]} ${jYear}`;
                  },

                  // Day format in month view (Day number)
                  dayFormat: (date) => {
                    const day = moment(date).jDate();
                    console.log("day", day);
                    return `${day}`;
                  },

                  // Weekday header format (Sat, Sun, etc.)
                  weekdayFormat: (date) => {
                    const weekdays = [
                      "شنبه",
                      "یکشنبه",
                      "دوشنبه",
                      "سه‌شنبه",
                      "چهارشنبه",
                      "پنجشنبه",
                      "جمعه",
                    ];
                    return weekdays[moment(date).day()];
                  },

                  // Day header in week/day view (Saturday, 15 Hamal)
                  dayHeaderFormat: (date) => {
                    const weekdays = [
                      "شنبه",
                      "یکشنبه",
                      "دوشنبه",
                      "سه‌شنبه",
                      "چهارشنبه",
                      "پنجشنبه",
                      "جمعه",
                    ];
                    const dariMonths = [
                      "حمل",
                      "ثور",
                      "جوزا",
                      "سرطان",
                      "اسد",
                      "سنبله",
                      "میزان",
                      "عقرب",
                      "قوس",
                      "جدی",
                      "دلو",
                      "حوت",
                    ];
                    const day = moment(date).jDate();
                    const jMonth = moment(date).jMonth();
                    const weekday = weekdays[moment(date).day()];
                    return `${weekday}، ${day} ${dariMonths[jMonth]}`;
                  },

                  // Time format in week/day view
                  timeGutterFormat: (date) => {
                    return moment(date).format("HH:mm");
                  },

                  // Agenda header format
                  agendaHeaderFormat: ({ start, end }) => {
                    const dariMonths = [
                      "حمل",
                      "ثور",
                      "جوزا",
                      "سرطان",
                      "اسد",
                      "سنبله",
                      "میزان",
                      "عقرب",
                      "قوس",
                      "جدی",
                      "دلو",
                      "حوت",
                    ];

                    const startDay = moment(start).jDate();
                    const startMonth = moment(start).jMonth();
                    const startYear = moment(start).jYear();
                    const endDay = moment(end).jDate();
                    const endMonth = moment(end).jMonth();
                    const endYear = moment(end).jYear();

                    if (startMonth === endMonth && startYear === endYear) {
                      return `${startDay} - ${endDay} ${dariMonths[startMonth]} ${startYear}`;
                    }
                    if (startYear === endYear) {
                      return `${startDay} ${dariMonths[startMonth]} - ${endDay} ${dariMonths[endMonth]} ${startYear}`;
                    }
                    return `${startDay} ${dariMonths[startMonth]} ${startYear} - ${endDay} ${dariMonths[endMonth]} ${endYear}`;
                  },

                  // Day range header
                  dayRangeHeaderFormat: ({ start, end }) => {
                    const dariMonths = [
                      "حمل",
                      "ثور",
                      "جوزا",
                      "سرطان",
                      "اسد",
                      "سنبله",
                      "میزان",
                      "عقرب",
                      "قوس",
                      "جدی",
                      "دلو",
                      "حوت",
                    ];

                    const startDay = moment(start).jDate();
                    const startMonth = moment(start).jMonth();
                    const endDay = moment(end).jDate();
                    const endMonth = moment(end).jMonth();

                    if (startMonth === endMonth) {
                      return `${startDay} - ${endDay} ${dariMonths[startMonth]}`;
                    }
                    return `${startDay} ${dariMonths[startMonth]} - ${endDay} ${dariMonths[endMonth]}`;
                  },

                  // Tooltip date format
                  dateFormat: (date) => {
                    const dariMonths = [
                      "حمل",
                      "ثور",
                      "جوزا",
                      "سرطان",
                      "اسد",
                      "سنبله",
                      "میزان",
                      "عقرب",
                      "قوس",
                      "جدی",
                      "دلو",
                      "حوت",
                    ];
                    const day = moment(date).jDate();
                    const jMonth = moment(date).jMonth();
                    const jYear = moment(date).jYear();
                    return `${day}`;
                    return `${day} ${dariMonths[jMonth]} ${jYear}`;
                  },
                }}
              />
            </div>
          </div>
          <div
            className=" border rounded-t-sm mt-8 "
            style={{ direction: "rtl" }}
          >
            <p
              className="bg-gray-50 border-b rounded-t-sm px-2 py-1 mb-4"
              dir="rtl"
            >
              گراف رشد
            </p>
            <div className="p-2">
              <ResponsiveContainer width="100%" height={500}>
                <AreaChart
                  data={billsGrowth}
                  layout="horizontal" // Keep horizontal layout
                >
                  <CartesianGrid />
                  <XAxis
                    dataKey="name"
                    reversed={true} // Reverse the X-axis for RTL
                    tick={{ textAnchor: "middle" }} // Center text alignment
                  />
                  <YAxis
                    orientation="right" // Move Y-axis to right side for RTL
                    tick={{ textAnchor: "end" }} // Adjust tick alignment
                  />
                  <Tooltip
                    wrapperStyle={{ direction: "rtl" }} // RTL for tooltip
                    labelFormatter={(label) => `ماه: ${label}`} // Custom Persian label
                    formatter={(value) => [`${value.toLocaleString()}`, "مبلغ"]} // Format values
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    fill="#AFEEEE"
                    stroke="#6cb2eb"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="lg:w-1/2">
          <DueBills />
          <DuePurchases />
          <LastExpenses />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
